import ArchiveIcon from "@mui/icons-material/ArchiveOutlined";
import DeleteIcon from "@mui/icons-material/DeleteOutlineRounded";
import UnarchiveIcon from "@mui/icons-material/UnarchiveOutlined";
import { Button, Dialog, DialogContent, DialogTitle, Divider } from "@mui/material";
import { useState } from "react";
import { withErrorHandling } from "@entrilia/shared/api/axiosHelper";
import DialogButtons from "@entrilia/shared/components/DialogButtons";
import DialogCloseButton from "@entrilia/shared/components/DialogCloseButton";
import HorizontalFill from "@entrilia/shared/components/HorizontalFill";
import { useNotificationContext } from "@entrilia/shared/contexts/NotificationContext";
import { logError } from "@entrilia/shared/logging";
import { distinct } from "@entrilia/shared/utilities/arrayHelper";
import adminApi from "../../../../../api/adminApi";
import type { Metric } from "../../../../../api/types/portfolioMonitoringTypes";
import { useMetricsPageContext } from "../MetricsPageContext";
import MetricEditor from "./MetricEditor";
import type { MetricEditorFormState } from "./metricEditorHelper";
import { getInitialMetricEditorFormStateFromMetric, mapFormToUpdateMetricRequest } from "./metricEditorHelper";

interface Props {
  metric: Metric;
  onClose: () => void;
  onUpdated: (metric: Metric) => void;
}

const updateMetric = withErrorHandling(adminApi.updateMetric);

const EditMetricDialog = ({ metric, onClose, onUpdated }: Props) => {
  const { sendNotification, sendNotificationError } = useNotificationContext();
  const { metrics, onDeleteMetric, onDeactivateMetric } = useMetricsPageContext();

  const [saving, setSaving] = useState(false);
  const [reactivating, setReactivating] = useState(false);

  const [formState, setFormState] = useState<MetricEditorFormState>(() =>
    getInitialMetricEditorFormStateFromMetric(metric)
  );

  const handleDeleteMetricClick = () => {
    onClose();
    onDeleteMetric(metric);
  };

  const handleDeactivateMetricClick = () => {
    onClose();
    onDeactivateMetric(metric);
  };

  const handleReactivateMetric = async () => {
    setReactivating(true);
    const [updatedMetric, error] = await updateMetric(metric.id, { inactive: false });
    setReactivating(false);

    if (error) {
      sendNotificationError(error.message);
      logError(error, "[EditMetricDialog] reactivateMetric");
    } else {
      sendNotification(`Metric '${metric.name}' reactivated successfully`);
      onUpdated(updatedMetric);
    }
  };

  const handleUpdateMetric = async () => {
    if (!formState.isFormValid) {
      return;
    }

    setSaving(true);
    const [updatedMetric, error] = await updateMetric(metric.id, mapFormToUpdateMetricRequest(formState));
    setSaving(false);

    if (error) {
      sendNotificationError(error.message);
      logError(error, "[EditMetricDialog] updateMetric");
    } else {
      sendNotification(`Metric '${metric.name}' updated successfully`);
      onUpdated(updatedMetric);
    }
  };

  const otherMetricNames = metrics.filter((m) => m.id !== metric.id).map((m) => m.name);
  const allMetricCategories = distinct(metrics.map((m) => m.category).filter(Boolean)) as string[];

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Edit Metric</DialogTitle>
      <DialogCloseButton onClick={onClose} />
      <Divider />

      <DialogContent>
        <MetricEditor
          form={formState}
          onChange={setFormState}
          otherMetricNames={otherMetricNames}
          allMetricCategories={allMetricCategories}
          editedMetric={metric}
        />
      </DialogContent>

      <Divider />
      <DialogButtons>
        {metric.inactive ? (
          <>
            <Button
              variant="outlined"
              color="secondary"
              loading={reactivating}
              onClick={handleReactivateMetric}
              startIcon={<UnarchiveIcon />}
            >
              Reactivate
            </Button>
            <Button variant="outlined" color="error" onClick={handleDeleteMetricClick} startIcon={<DeleteIcon />}>
              Delete
            </Button>
          </>
        ) : (
          <Button variant="outlined" color="error" onClick={handleDeactivateMetricClick} startIcon={<ArchiveIcon />}>
            Deactivate
          </Button>
        )}
        <HorizontalFill />
        <Button variant="text" color="secondary" onClick={onClose}>
          Cancel
        </Button>
        <Button
          disabled={!formState.isFormValid || formState.touchedFields.length === 0}
          variant="contained"
          loading={saving}
          onClick={handleUpdateMetric}
        >
          Save
        </Button>
      </DialogButtons>
    </Dialog>
  );
};

export default EditMetricDialog;
